import React, { useEffect, useState, useCallback } from 'react';
import BeneficiaryMultiValues from '../BeneficiaryValues/BeneficiaryMultiValues';
import PercentageFieldWithLabel from '../PercentageFieldWithLabel';
import {
  Beneficiary,
  BeneficiaryAge,
  BeneficiaryPercentage
} from '../utils/beneficiaryInterfaces';
import {
  getBeneficiaryValue,
  setBeneficiaryValue
} from '../utils/helperFunctions';
import { BeneficiaryAgeProps } from './BeneficiaryAdultValues';

const isDisabled = (
  name: Beneficiary<BeneficiaryAge>['name'],
  childrenAndYouth: BeneficiaryPercentage,
  children: BeneficiaryPercentage,
  youth: BeneficiaryPercentage
): boolean => {
  switch (name) {
    case 'Children and youth':
      return !!(children || youth);
    case 'Children':
    case 'Adolescents':
      return !!childrenAndYouth;
    default:
      return true;
  }
};

const BeneficiaryChildrenValues: React.FC<BeneficiaryAgeProps> = ({
  beneficiary,
  setBeneficiaries
}) => {
  const [childrenAndYouth, setChildrenAndYouth] = useState<
    BeneficiaryPercentage
  >(getBeneficiaryValue(beneficiary?.categories, 'Children and youth'));
  const [children, setChildren] = useState<BeneficiaryPercentage>(
    getBeneficiaryValue(beneficiary?.categories, 'Children')
  );
  const [youth, setYouth] = useState<BeneficiaryPercentage>(
    getBeneficiaryValue(beneficiary?.categories, 'Adolescents')
  );
  const [errorMessage] = useState<string | undefined>();

  const saveBeneficiaryChildren = useCallback(
    (category: BeneficiaryAge, percentage: BeneficiaryPercentage) => {
      setBeneficiaries(s => ({
        ...s,
        age: {
          categories: setBeneficiaryValue(
            s.age?.categories,
            category,
            percentage
          )
        }
      }));
    },
    [setBeneficiaries]
  );

  const saveChildrenAndYouth = useCallback(
    (percentage: BeneficiaryPercentage) => {
      saveBeneficiaryChildren('Children and youth', percentage);
      setChildrenAndYouth(percentage);
    },
    [beneficiary?.categories, setBeneficiaries, setChildrenAndYouth]
  );
  const saveChildren = useCallback(
    (percentage: BeneficiaryPercentage) => {
      saveBeneficiaryChildren('Children', percentage);
      setChildren(percentage);
    },
    [beneficiary?.categories, setBeneficiaries, setChildren]
  );
  const saveYouth = useCallback(
    (percentage: BeneficiaryPercentage) => {
      saveBeneficiaryChildren('Adolescents', percentage);
      setYouth(percentage);
    },
    [beneficiary, setBeneficiaries, setYouth]
  );
  const handleReset = useCallback(() => {
    saveChildrenAndYouth(null);
    saveChildren(null);
    saveYouth(null);
  }, [saveChildrenAndYouth, saveChildren, saveYouth]);

  return (
    <BeneficiaryMultiValues
      title="Children &amp; youth"
      errorMessage={errorMessage}
      leftSide={
        <PercentageFieldWithLabel
          label="All Children"
          initialValue={childrenAndYouth}
          onBlur={saveChildrenAndYouth}
          disabled={isDisabled(
            'Children and youth',
            childrenAndYouth,
            children,
            youth
          )}
        />
      }
      rightSide={
        <>
          <PercentageFieldWithLabel
            label="Children (0-9)"
            initialValue={children}
            // focusOnMount={true}
            onBlur={saveChildren}
            disabled={isDisabled('Children', childrenAndYouth, children, youth)}
          />
          <PercentageFieldWithLabel
            label="Youth (10+)"
            initialValue={youth}
            onBlur={saveYouth}
            className="field-margin-top"
            disabled={isDisabled(
              'Adolescents',
              childrenAndYouth,
              children,
              youth
            )}
          />
        </>
      }
      onReset={handleReset}
    />
  );
};

export default BeneficiaryChildrenValues;
